/**
 * Атмосферный фон экрана входа: золотое сияние улья, частицы и параллакс курсора.
 * Вдохновлено lunara-login (MIT), адаптировано под палитру УЛЬЯ без внешних ассетов.
 */

import { useEffect, useRef, type ReactNode } from "react";

import { useReducedMotion } from "./useReducedMotion.js";

export interface AuthSceneProps {
  readonly children: ReactNode;
}

interface Mote {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  phase: number;
}

export function AuthScene({ children }: AuthSceneProps): React.JSX.Element {
  const sceneRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    const scene = sceneRef.current;
    const canvas = canvasRef.current;
    if (!scene || !canvas || reducedMotion) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const motes: Mote[] = Array.from({ length: 120 }, () => ({
      x: Math.random(),
      y: Math.random(),
      vx: (Math.random() - 0.5) * 0.00008,
      vy: (Math.random() - 0.5) * 0.00006 - 0.00002,
      r: 0.6 + Math.random() * 1.8,
      phase: Math.random() * Math.PI * 2,
    }));

    let raf = 0;
    let pointerX = 0.5;
    let pointerY = 0.5;
    let px = 0;
    let py = 0;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const { width, height } = scene.getBoundingClientRect();
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const onMove = (event: PointerEvent) => {
      const rect = scene.getBoundingClientRect();
      pointerX = (event.clientX - rect.left) / rect.width;
      pointerY = (event.clientY - rect.top) / rect.height;
      const tiltX = (pointerX - 0.5) * 14;
      const tiltY = (pointerY - 0.5) * -10;
      scene.style.setProperty("--px", `${tiltX}px`);
      scene.style.setProperty("--py", `${tiltY}px`);
      scene.style.setProperty("--mx", `${pointerX * 100}%`);
      scene.style.setProperty("--my", `${pointerY * 100}%`);
      scene.style.setProperty("--rx", `${tiltY * 0.35}deg`);
      scene.style.setProperty("--ry", `${tiltX * 0.35}deg`);
    };

    const tick = (time: number) => {
      const { width, height } = scene.getBoundingClientRect();
      ctx.clearRect(0, 0, width, height);

      px += (pointerX - px) * 0.04;
      py += (pointerY - py) * 0.04;

      for (const mote of motes) {
        mote.x += mote.vx;
        mote.y += mote.vy;
        if (mote.x < 0 || mote.x > 1) mote.vx *= -1;
        if (mote.y < 0 || mote.y > 1) mote.vy *= -1;

        const dx = mote.x - px;
        const dy = mote.y - py;
        const dist = Math.hypot(dx, dy);
        const pull = Math.max(0, 0.22 - dist) * 2.2;
        const x = (mote.x + dx * pull * 0.08) * width;
        const y = (mote.y + dy * pull * 0.08) * height;
        const tw = 0.35 + 0.65 * (0.5 + 0.5 * Math.sin(time * 0.001 + mote.phase));
        const alpha = (0.12 + pull * 0.55) * tw;

        ctx.beginPath();
        ctx.fillStyle = `rgba(242, 193, 78, ${alpha})`;
        ctx.arc(x, y, mote.r + pull * 3, 0, Math.PI * 2);
        ctx.fill();
      }

      raf = requestAnimationFrame(tick);
    };

    resize();
    window.addEventListener("resize", resize);
    scene.addEventListener("pointermove", onMove);
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      scene.removeEventListener("pointermove", onMove);
    };
  }, [reducedMotion]);

  return (
    <div className="auth-scene" ref={sceneRef}>
      <canvas ref={canvasRef} className="auth-scene__dust" aria-hidden="true" />
      <div className="auth-scene__glow" aria-hidden="true" />
      <div className="auth-scene__grid" aria-hidden="true" />
      <div className="auth-scene__stage auth-scene__stage--in">{children}</div>
    </div>
  );
}
